import type { Booking } from "../types";
import { toLocalDateString, toStrictYYYYMMDD } from "../src/dateUtils";
import { canonicalizeBookingStatusInput } from "./bookingStatusNormalize";

/** TEMP DEBUG: app-id filter disabled — restore after confirming server returns rows. */
export function bookingPassesAdminCalendarAppIdFilter(_b: Booking): boolean {
  return true;
}

function sliceYyyyMmDdPrefix(s: string): string | null {
  const t = s.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) return t.slice(0, 10);
  return null;
}

/**
 * Normalize Netlify / Firestore-shaped rows for admin calendar, agenda, and client overview.
 * Fixes Timestamp JSON / odd `rawDate` shapes so `YYYY-MM-DD` comparisons match `adminSelectedDate`.
 * Prefer strict calendar keys (no timezone drift vs the grid) when the wire value allows it.
 */
export function normalizeBookingRowForAdminUi(b: Booking): Booking {
  const preferred = (b as Booking).preferredDate;
  const displayDate = (b as Booking).date;
  const rawDateStr =
    toStrictYYYYMMDD(b.rawDate as unknown) ??
    toLocalDateString(b.rawDate as unknown) ??
    sliceYyyyMmDdPrefix(typeof b.rawDate === "string" ? b.rawDate : "") ??
    toStrictYYYYMMDD(preferred as unknown) ??
    toLocalDateString(preferred as unknown) ??
    sliceYyyyMmDdPrefix(typeof preferred === "string" ? preferred : "") ??
    toStrictYYYYMMDD(displayDate as unknown) ??
    toLocalDateString(displayDate as unknown) ??
    sliceYyyyMmDdPrefix(typeof displayDate === "string" ? displayDate : "") ??
    "";
  const t = rawDateStr.trim();
  /** One canonical grid key: strict YYYY-MM-DD (matches Calendar `getDateString`). */
  const generatedKey =
    (t && (toStrictYYYYMMDD(t) ?? sliceYyyyMmDdPrefix(t) ?? "")) || "";
  const status = canonicalizeBookingStatusInput(b.status) ?? b.status;
  const userEmail = (b.userEmail || "").toLowerCase().trim() || b.userEmail;
  return { ...b, rawDate: generatedKey, status, userEmail };
}

/**
 * After `normalizeBookingRowForAdminUi`, decide if the row belongs in admin `globalBookings` (API + Firestore merge).
 * TEMP DEBUG: include every row except canonical `Deleted` (app-id / active / soft-delete checks disabled).
 */
export function shouldIncludeNormalizedAdminBooking(b: Booking): boolean {
  const status = canonicalizeBookingStatusInput(b.status) ?? b.status;
  if (status === "Deleted") return false;
  if (!bookingPassesAdminCalendarAppIdFilter(b)) return false;
  return true;
}

/** Strict YYYY-MM-DD for calendar grid keys, or null if the row cannot be placed on the grid. */
export function adminCalendarGridDateKey(b: Booking): string | null {
  const k = bookingCalendarDateKey(b).trim();
  const strict = toStrictYYYYMMDD(k) ?? sliceYyyyMmDdPrefix(k);
  return strict && /^\d{4}-\d{2}-\d{2}$/.test(strict) ? strict : null;
}

/**
 * Calendar / agenda / overview: always derive from `normalizeBookingRowForAdminUi` so dots and lists never diverge.
 */
export function bookingCalendarDateKey(b: Booking | (Partial<Booking> & { rawDate?: unknown })): string {
  return normalizeBookingRowForAdminUi(b as Booking).rawDate;
}
